// Sleeve Calendar — conviction-based 30d lookahead.
// Terminal aesthetic: monochrome, dense, tabular, minimal decoration.
// Covers: FOMC, CPI, Beige Book, OpEx, Month Start/End, Quarter End,
// Pre-Holiday, Complacency, Fear Monitor, Contract Rollover.

import { useMemo } from "react";
import type { Algo } from "../api/types";

const LOOKAHEAD_DAYS = 30;

// ── Known 2026 dates ──────────────────────────────────────────────────

const FOMC: string[] = ["2026-09-16","2026-11-04","2026-12-16"];
const CPI: string[] = [
  "2026-09-10","2026-10-10","2026-11-10","2026-12-10",
];
const HOLIDAYS: string[] = [
  "2026-09-07","2026-11-26","2026-12-25",
];

// FOMC minutes come out ~3 weeks after the meeting
function fomcMinutesDates(): string[] {
  return FOMC.map(d => {
    const dt = new Date(d + "T12:00:00Z");
    dt.setDate(dt.getDate() + 21);
    return dt.toISOString().slice(0, 10);
  });
}

function bbDates(): string[] {
  return FOMC.map(d => {
    const dt = new Date(d + "T12:00:00Z");
    dt.setDate(dt.getDate() - 14);
    return dt.toISOString().slice(0, 10);
  });
}

function opexDates(): string[] {
  const r: string[] = [];
  for (let m = 8; m <= 12; m++) {
    const d = new Date(2026, m, 1);
    let fc = 0;
    while (d.getMonth() === m) {
      if (d.getDay() === 5 && ++fc === 3) {
        const s = d.toISOString().slice(0, 10);
        if (s >= "2026-08-24") r.push(s);
        break;
      }
      d.setDate(d.getDate() + 1);
    }
  }
  return r;
}

// Contract rollover dates: ~5 days before quarterly expiry
function rolloverDates(): string[] {
  const qtrs = [
    [2026, 9], [2026, 12], [2027, 3],
  ];
  return qtrs.map(([y, m]) => {
    const d = new Date(y, m - 1, 1);
    let fc = 0;
    while (d.getMonth() === m - 1) {
      if (d.getDay() === 5 && ++fc === 3) break;
      d.setDate(d.getDate() + 1);
    }
    d.setDate(d.getDate() - 5);
    return d.toISOString().slice(0, 10);
  });
}

function isLastTradingDay(d: Date, h: Set<string>): boolean {
  const n = new Date(d);
  n.setDate(n.getDate() + 1);
  while (n.getDay() === 0 || n.getDay() === 6 || h.has(n.toISOString().slice(0, 10))) {
    n.setDate(n.getDate() + 1);
  }
  return n.getMonth() !== d.getMonth();
}

function isLastQtr(d: Date, h: Set<string>): boolean {
  return isLastTradingDay(d, h) && [2, 5, 8, 11].includes(d.getMonth());
}

type Conviction = "high" | "medium";

interface Ev {
  label: string;
  conviction: Conviction;
  detail: string;
}

export function SleeveCalendarPanel({ algos: _algos }: { algos: Algo[] }) {
  const days = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const h = new Set(HOLIDAYS);
    const bb = new Set(bbDates());
    const op = new Set(opexDates());
    const fomcSet = new Set(FOMC);
    const cpiSet = new Set(CPI);
    const fomcMinSet = new Set(fomcMinutesDates());
    const rolloverSet = new Set(rolloverDates());

    const has = (kw: string) => _algos.some(a => a.name.toLowerCase().includes(kw) || a.strategy.includes(kw));
    const fomcSleeve = has("fomc");
    const cpiSleeve = has("cpi");
    const bbSleeve = has("beige");
    const opexSleeve = has("opex");
    const msSleeve = has("month_start");
    const meSleeve = has("month_end");
    const qeSleeve = has("quarter_end");
    const phSleeve = has("pre_holiday");
    const compSleeve = has("complacency");
    const fearSleeve = _algos.some(a =>
      a.strategy.includes("flight_to_safety") ||
      a.strategy.includes("credit_fear") ||
      a.strategy.includes("fear_cond_opening_drift")
    );

    const out: { ds: string; evs: Ev[] }[] = [];

    for (let i = 0; i < LOOKAHEAD_DAYS; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const ds = d.toISOString().slice(0, 10);
      const wd = d.getDay() === 0 || d.getDay() === 6;
      if (wd || h.has(ds)) continue;
      const evs: Ev[] = [];

      // HIGH conviction — calendar-anchored
      if (fomcSleeve && fomcSet.has(ds))
        evs.push({ label: "FOMC", conviction: "high", detail: "FOMC Long + Post-FOMC Short + FOMC Tone Short MNQ/MGC 14:00-15:55 ET" });
      if (fomcSleeve && fomcMinSet.has(ds))
        evs.push({ label: "FOMC MINUTES", conviction: "high", detail: "FOMC Minutes MNQ 14:00-13:55 ET" });
      if (bbSleeve && bb.has(ds))
        evs.push({ label: "BEIGE BOOK", conviction: "high", detail: "Beige Book MNQ 14:05 ET exit 15:55 ET" });
      if (cpiSleeve && cpiSet.has(ds))
        evs.push({ label: "CPI", conviction: "high", detail: "CPI Dir MNQ + MGC 08:30-09:30 ET" });
      if (opexSleeve && op.has(ds))
        evs.push({ label: "OPEX", conviction: "high", detail: "OpEx Short MNQ 09:30-15:55 ET" });
      if (msSleeve && d.getDate() <= 3 && d.getDay() >= 1 && d.getDay() <= 5)
        evs.push({ label: "MONTH START", conviction: "high", detail: "Month Start Long MNQ 09:30-15:55 ET (days 1-3)" });
      if (meSleeve && isLastTradingDay(d, h))
        evs.push({ label: "MONTH END", conviction: "high", detail: "Month End Rebalance MNQ 13:00-15:55 ET" });
      if (qeSleeve && isLastQtr(d, h))
        evs.push({ label: "QUARTER END", conviction: "high", detail: "Q-End Rebal MNQ + MGC 13:00-15:30 ET" });
      if (phSleeve) {
        const n = new Date(d);
        n.setDate(n.getDate() + 1);
        while (n.getDay() === 0 || n.getDay() === 6) n.setDate(n.getDate() + 1);
        if (h.has(n.toISOString().slice(0, 10)))
          evs.push({ label: "PRE-HOLIDAY", conviction: "high", detail: "Pre-Holiday Drift MNQ 11:00-16:00 ET LONG" });
      }
      if (rolloverSet.has(ds))
        evs.push({ label: "CONTRACT ROLL", conviction: "medium", detail: "Front-month contract roll ~5d pre-expiry — MNQ/MES roll to next quarter" });

      // MEDIUM conviction — condition-gated
      if (compSleeve && d.getDay() === 1)
        evs.push({ label: "COMPLACENCY", conviction: "medium", detail: "SHORT MGC Mon open if VIX sma10<13.5 & SKEW<120, exit Fri close" });
      if (fearSleeve)
        evs.push({ label: "FEAR MONITOR", conviction: "medium", detail: "Flight/Credit/Fear-Cond sleeves watching VIX/HY OAS/TED/DXY-JPY" });

      if (evs.length) out.push({ ds, evs });
    }
    return out;
  }, [_algos]);

  if (days.length === 0) return (
    <div className="card">
      <h2>Sleeve Calendar</h2>
      <div className="muted small">No events in next 30 days</div>
    </div>
  );

  const isWeekendToday = [0, 6].includes(new Date().getDay());
  const todayDs = new Date().toISOString().slice(0, 10);

  return (
    <div className="card">
      <h2 style={{ flexShrink: 0 }}>Sleeve Calendar</h2>
      <div className="muted small" style={{ flexShrink: 0, marginBottom: 4 }}>
        {days.length} event days · {LOOKAHEAD_DAYS}d · <span style={{ color: "var(--bad)" }}>HIGH</span> <span style={{ color: "var(--warn)" }}>MED</span>
      </div>
      {isWeekendToday && (
        <div className="muted small" style={{ marginBottom: 4, fontStyle: "italic", borderTop: "1px solid var(--border)", paddingTop: 4 }}>
          Markets closed today — showing upcoming events
        </div>
      )}

      <div className="scroll-vert">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
          <tbody>
            {days.map((day, i) => (
              <tr key={day.ds}>
                <td className="mono" style={{
                  padding: "2px 4px 2px 0", whiteSpace: "nowrap", verticalAlign: "top",
                  color: i === 0 && day.ds === todayDs ? "var(--accent)" : "var(--fg-primary)", fontWeight: i === 0 && day.ds === todayDs ? 700 : 500,
                  fontSize: i === 0 && day.ds === todayDs ? 10 : 9,
                  borderBottom: "1px solid var(--border)",
                }}>
                  {i === 0 && day.ds === todayDs ? "TODAY" : day.ds.slice(5)}
                </td>
                <td style={{ padding: "2px 0", borderBottom: "1px solid var(--border)" }}>
                  {day.evs.map((ev, ei) => (
                    <div key={ei} title={ev.detail} style={{ display: "flex", alignItems: "center", gap: 4, padding: "1px 0", lineHeight: 1.4 }}>
                      <span style={{
                        color: ev.conviction === "high" ? "var(--bad)" : "var(--warn)",
                        fontWeight: 600, fontSize: 8, letterSpacing: "0.03em", whiteSpace: "nowrap", flexShrink: 0,
                      }}>
                        {ev.label}
                      </span>
                      <span className="mono muted" style={{ fontSize: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {ev.detail}
                      </span>
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}